import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { Button, Flex, Page, Typo, PersonalMessage, theme, Figure, PageHeaderLogo } from '../../ui';
import Loading from '../../ui/Loading';
import { useIsLoggedIn, useUser } from '../auth/hooks';
import { fetchStore, selectStoreOwner, selectUserTheme } from '../webshop/globalSlice';
import { useInvitesModalState, useInvitesActions } from './hooks';
import { useSetLoginModal } from '../global/hooks';
import RegistrationForm from '../../components/RegistrationForm';
import { media } from '../../utils/media';
import PageContainerVCenter from '../../ui/Page/PageContainerVCenter';
import { Trans, useTranslation } from 'react-i18next';
import { STATUS_LOADING } from '../../utils/constants';
import { useLogout } from '../auth/hooks/useAuthActions';
import { useAuthActionsContext } from '../auth/context/AuthContext';

const InvitationWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: flex-start;
  gap: 80px;
  width: 100%;
  justify-content: space-between;
  background-color: ${theme.colors.NEU_0};
  padding: 40px;
  ${media.lessThan('phablet')} {
    flex-direction: column;
    padding: 20px;
  }
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 100px;
  width: 70%;
  align-items: flex-start;
  background-color: ${theme.colors.NEU_0};
  padding: 40px;
  ${media.lessThan('phablet')} {
    width: 100%;
    padding: 20px;
  }
`;

const AcceptInvitation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { hash } = useParams();

  const logout = useLogout();
  const setLoginModal = useSetLoginModal();
  const authActions = useAuthActionsContext();
  const { checkInvitation, acceptInvitation } = useInvitesActions();

  const { modalState } = useInvitesModalState();
  const invitationFeedback = modalState?.redeemInvitation || {};
  const loggedIn = useIsLoggedIn();
  const user = useUser();
  const userEmail = user?.email?.toLowerCase();
  const webshopOwner = useSelector(selectStoreOwner);
  const webshopTheme = useSelector(selectUserTheme);
  const [errorMessage, setErrorMessage] = useState(null);
  const checkInvStatus = modalState?.checkStatus || 'idle';
  const { expired, course, used, usedByMe, limit, email, count, errMsg } = invitationFeedback;
  const invitationEmail = email && email !== 'Not provided' ? email.toLowerCase() : email;
  const giftCodeError = modalState?.giftCodeError;

  useEffect(() => {
    const checkInvitationHandler = async () => {
      if (hash) {
        try {
          const result = await checkInvitation({ hash });
          const ownerInfo = result && result[0]?.owner;
          if (ownerInfo) {
            dispatch(fetchStore({ store: ownerInfo.username }));
          }
        } catch (error) {
          // Error is already handled in useInvitesActions and stored in modalState
          // The UI will display the error message from invitationFeedback.errMsg
          console.error('Failed to check invitation:', error);
        }
      }
    };

    checkInvitationHandler();
  }, [hash, checkInvitation, dispatch]);

  const handleRequestNewInvitation = useCallback(() => {
    const ownerEmail = invitationFeedback.owner.email;
    document.location = `mailto: ${ownerEmail}`;
  }, [invitationFeedback]);

  const handleLogin = () => {
    // Store the current invitation hash so we can redirect back after login
    if (hash) {
      localStorage.setItem('pendingInvitationHash', hash);
    }
    loggedIn && logout();
    setLoginModal(true);
  };

  const handleGoToStartPage = () => {
    navigate(`/dashboard`);
  };

  const handleAccept = async () => {
    const result = await acceptInvitation({ hash });
    const navigatePath = `/view/course/${course.id}`;

    if (!result.errorCode) {
      navigate(navigatePath);
    } else {
      setErrorMessage(result.response.errMsg);
    }
  };

  return (
    <Page backgroundColor={theme.colors.PRIMARY} fullheight={true}>
      <PageHeaderLogo marginBottom='1rem' />
      <PageContainerVCenter>
        {errMsg || giftCodeError || errorMessage ? (
          <ErrorWrapper>
            <Typo.PageHeader>{t('globals.invitation')}</Typo.PageHeader>
            <PersonalMessage>
              <Typo.BigText>{errMsg || giftCodeError || errorMessage}</Typo.BigText>
            </PersonalMessage>
            <Flex width='100%' justify='center'>
              {loggedIn ? <Button onClick={handleGoToStartPage}>{t('invites.goToStart')}</Button> : <Button onClick={handleLogin}>{t('invites.login')}</Button>}
            </Flex>
          </ErrorWrapper>
        ) : (
          <InvitationWrapper>
            {checkInvStatus === STATUS_LOADING ? (
              <Flex width='100%' justify='center'>
                <Loading />
              </Flex>
            ) : (
              <>
                {!!webshopTheme.logoUrl && (
                  <Flex width='200px' breakPoint={{ size: 'phablet', value: 'width:150px; align-self:center;' }}>
                    <Figure.Circle $objectfit='contain' shadow='true' background={theme.colors.NEU_0}>
                      <img src={webshopTheme.logoUrl} alt={webshopOwner.username} loading='lazy' />
                    </Figure.Circle>
                  </Flex>
                )}
                <Flex direction='column' gap='40' align='flex-start' width='100%'>
                  {email && email !== 'Not provided' && (
                    <>
                      <Typo.PageHeader>{t('globals.invitation')}</Typo.PageHeader>
                      <Flex gap='10' breakPoint={{ size: 'phablet', value: 'flex-direction:column' }}>
                        <Typo.ContentBigHeader>{t('invites.invitationSentTo')}:</Typo.ContentBigHeader>
                        <Typo.BigText>{email}</Typo.BigText>
                      </Flex>
                    </>
                  )}
                  <Flex direction='column' gap='50' align='flex-start' width='100%'>
                    {count === limit && used && (!usedByMe || !loggedIn) && (
                      <>
                        <PersonalMessage>
                          <Typo.BigText>{t('invites.alreadyAcceptedInvitation_notLoggedIn')}</Typo.BigText>
                        </PersonalMessage>
                        <Button onClick={handleLogin}>{t('invites.login')}</Button>
                      </>
                    )}

                    {count === limit && used && usedByMe && (
                      <>
                        <PersonalMessage>
                          <Typo.BigText>{t('invites.alreadyAcceptedInvitation_loggedIn')}</Typo.BigText>
                        </PersonalMessage>
                        <Button onClick={handleGoToStartPage}>{t('invites.goToStart')}</Button>
                      </>
                    )}

                    {loggedIn && !expired && !used && (userEmail === invitationEmail || email === 'Not provided') && (
                      <>
                        <PersonalMessage>
                          <Typo.BigText>{t('invites.acceptInvitationBellow')}</Typo.BigText>
                        </PersonalMessage>
                        <Button onClick={handleAccept}>{t('invites.accept')}</Button>
                      </>
                    )}

                    {loggedIn && !expired && !used && userEmail !== invitationEmail && email !== 'Not provided' && (
                      <>
                        <PersonalMessage>
                          <Typo.BigText>{t('invites.invitationEmailMismatch')}</Typo.BigText>
                          <Typo.Text>
                            <Trans
                              t={t}
                              i18nKey={'invites.invitationEmailMismatchMessage'}
                              values={{
                                invitationEmail: email,
                                loggedInEmail: userEmail,
                              }}
                            />
                          </Typo.Text>
                        </PersonalMessage>
                      </>
                    )}

                    {!loggedIn && !used && !expired && (
                      <>
                        <PersonalMessage>
                          <Typo.BigText>{t('invites.acceptInvitationNeedsAccount')}</Typo.BigText>
                        </PersonalMessage>
                        {course && (
                          <div>
                            <Typo.TextTitle>{t('globals.invitation')}</Typo.TextTitle>
                            <Typo.MediumText>
                              <Trans t={t} i18nKey={'registration.afterRegister'} values={{ courseName: course.name }} components={{ bold: <strong /> }} />
                            </Typo.MediumText>
                          </div>
                        )}
                        <RegistrationForm email={email} handleLogin={handleLogin} createAccountCall={authActions.registerAndLoginWithoutRedirect} />
                      </>
                    )}

                    {expired && !used && (
                      <>
                        <PersonalMessage>
                          <Typo.BigText>{t('invites.expiredInvitation')}</Typo.BigText>
                          <Typo.Text>{t('invites.newInvitationContactOwner')}</Typo.Text>
                        </PersonalMessage>

                        <Flex gap='40'>
                          <Button onClick={handleRequestNewInvitation}>{t('invites.requestNew')}</Button>
                          {loggedIn && userEmail === invitationEmail ? (
                            <Button onClick={handleGoToStartPage}>{t('invites.goToStart')}</Button>
                          ) : (
                            <Button onClick={handleLogin}>{t('invites.login')}</Button>
                          )}
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>
              </>
            )}
          </InvitationWrapper>
        )}
      </PageContainerVCenter>
    </Page>
  );
};

export default AcceptInvitation;
